import RssFeedCRUDActions from '@mm/state/actions/RssFeedCRUDActions';
import { unselectRssFeed } from '@mm/state/actions/viewActions';
import RssFeedModel from '@mm/state/model/RssFeedModel';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  CallToActionType,
  CampaignDisplayType,
} from '@mm/state/modules/modalsState/props';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers, withState } from 'recompose';
import { Dispatch } from 'redux';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { useActivePageMessagingStatus } from '../../../campaigns/components/BlastTypeSelector/BlastTypeSelector';
import {
  AUDIENCE_ERROR,
  NAME_ERROR,
  TOGGLE_ERROR,
  URL_ERROR,
} from '../../constants/texts';
import ActiveToggle from './ActiveToggle';
import DescriptionTextInput from './DescriptionTextInput';
import LinkTextInput from './LinkTextInput';
import NameTextInput from './NameTextInput';
import { RssAudienceSelector } from './RssAudienceSelector';
import './RssFeedDetails.scss';
import UrlTextInput from './UrlTextInput';
import WebviewHeightSelectInput from './WebviewHeightSelectInput';

export type Errors = {
  name?: string;
  url?: string;
  audience?: string;
  campaignLimitHit?: boolean;
  toggle?: string;
};

type PropsFromWithHandler = {
  isValid: ReturnType<typeof isValid>;
};

type PropsFromWithState = {
  errors: Errors;
  setErrors: (value: Errors) => Errors;
};

type OuterProps = {};
type InnerProps = PropsFromWithHandler &
  PropsFromWithState &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = InnerProps & OuterProps;

const RssFeedDetails = ({
  feed,
  isValid,
  errors,
  activeChatbotId,
  connectedPage,
  match: {
    params: { chatbotId, rssFeedId },
  },
  getFeedById,
  deselectRssFeed,
}: Props) => {
  useActivePageMessagingStatus();

  const approvedForMessaging =
    connectedPage.subscriptionMessagingStatus === 'approved';
  useEffect(() => {
    getFeedById(chatbotId, rssFeedId);

    return function deslectFeed() {
      deselectRssFeed();
    };
  }, []);

  return (
    <div>
      <div className="shared-panel__main-container">
        <div className="feed-details__main-container">
          {feed && (
            <div className="feed-details__content-wrapper">
              <ActiveToggle
                feed={feed}
                chatbotId={activeChatbotId}
                isValid={isValid}
                errors={errors}
                disabled={!feed.active && !approvedForMessaging}
              />
              <NameTextInput
                feed={feed}
                chatbotId={activeChatbotId}
                isValid={isValid}
              />
              <UrlTextInput
                feed={feed}
                chatbotId={activeChatbotId}
                validate={isValid}
                errors={errors}
              />
              <DescriptionTextInput feed={feed} chatbotId={activeChatbotId} />
              <LinkTextInput feed={feed} chatbotId={activeChatbotId} />
              <WebviewHeightSelectInput
                feed={feed}
                chatbotId={activeChatbotId}
              />
              <RssAudienceSelector
                feed={feed}
                chatbotId={activeChatbotId}
                validate={isValid}
                errors={errors}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const isValid =
  ({
    feed,
    setErrors,
    openCampaignExceedsSendLimitModal,
    audiences,
    messagesStatistic,
    activeFacebookPage,
    activeFacebookPagePlanInfo,
    connectedPage,
    openMonthlySendsWarningModal,
  }: InnerProps) =>
  (nextValue: Partial<RssFeedModel>) => {
    if (
      !feed.active &&
      connectedPage.subscriptionMessagingStatus !== 'approved'
    ) {
      return;
    }
    const errors: Errors = {};
    const nextFeed = Object.assign({}, feed, nextValue);
    const isNewBillingSubscription =
      activeFacebookPage?.subscription &&
      isBilling2023Subscription(activeFacebookPage.subscription);

    if (!nextFeed.name) {
      errors.name = NAME_ERROR;
    }
    if (!nextFeed.url) {
      errors.url = URL_ERROR;
    }
    if (!nextFeed.audienceId) {
      errors.audience = AUDIENCE_ERROR;
    }

    if (nextFeed.active && nextFeed.audienceId && messagesStatistic) {
      const audience = audiences.find(a => a.id === nextFeed.audienceId);
      const messagesRemaining = activeFacebookPage
        ? activeFacebookPage.messagesStatistic.messagesRemaining
        : Number.MAX_SAFE_INTEGER;

      if (
        activeFacebookPagePlanInfo.isFreePlan &&
        messagesStatistic.messagesRemaining === 0
      ) {
        openMonthlySendsWarningModal();
        errors.campaignLimitHit = true;
      } else if (
        !(
          activeFacebookPagePlanInfo.isCustomTeam ||
          activeFacebookPagePlanInfo.isLegacyTeamPlan
        ) &&
        messagesRemaining < audience.contactsCount &&
        !(activeFacebookPage && activeFacebookPage.overagesEnabled) &&
        !isNewBillingSubscription
      ) {
        errors.campaignLimitHit = true;

        return openCampaignExceedsSendLimitModal(
          'RSS Blaster',
          activeFacebookPagePlanInfo.isFreePlan ? 'upgrade' : 'enable_overage',
        );
      }
    }

    const allValid = Object.keys(errors).length === 0;
    setErrors(allValid ? {} : { ...errors, toggle: TOGGLE_ERROR });
    return allValid;
  };

const mapStateToProps = state => ({
  connectedPage: state.connectedPage.pageDetails,
  audiences: state.audiences.entities,
  feed: state.view.chatbotEditor.selectedRssFeed,
  activeChatbotId: activeChatbotIdSelector(state),
  messagesStatistic: activeMessagesStatisticSelector(state),
  activeFacebookPagePlanInfo: activeFacebookPagePlanInfoSelector(state).info,
  activeFacebookPage: activeFacebookPageSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  deselectRssFeed: () => dispatch(unselectRssFeed()),
  getFeedById: (botId, feedId) =>
    dispatch(RssFeedCRUDActions.retrieveById(botId, feedId)),
  openCampaignExceedsSendLimitModal: (
    campaignDisplayType: CampaignDisplayType,
    callToActionType: CallToActionType,
  ) =>
    dispatch(
      modalActions.openCampaignExceedsSendLimitModal(
        campaignDisplayType,
        callToActionType,
      ),
    ),
  openMonthlySendsWarningModal: () =>
    dispatch(modalActions.openMonthlySendsWarningModal()),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withState('errors', 'setErrors', {}),
  withHandlers({ isValid }),
);

export default enhance(RssFeedDetails);
